import { useEffect, useRef, useState } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const commonPrefix = (a, b) => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
};

// Reveals `full` character by character; speeds up when far behind so it never lags the speaker.
export function useTypewriter(full) {
  const [shown, setShown] = useState(full.length);
  const pos = useRef(full.length);
  const prev = useRef(full);

  useEffect(() => {
    pos.current = Math.min(pos.current, commonPrefix(prev.current, full));
    prev.current = full;
    if (reduced()) { pos.current = full.length; setShown(full.length); return; }
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(64, now - last);
      last = now;
      const backlog = full.length - pos.current;
      if (backlog <= 0) { setShown(full.length); return; }
      const cps = 38 + backlog * 4;
      pos.current = Math.min(full.length, pos.current + (cps * dt) / 1000);
      setShown(Math.floor(pos.current));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [full]);

  return Math.min(shown, full.length);
}

// Maps typed progress back onto items; only items the typewriter has reached are returned.
export function typedItems(items, shown) {
  let offset = 0;
  const out = [];
  for (const it of items) {
    if (shown <= offset && out.length) break;
    out.push({ ...it, typed: it.text.slice(0, Math.max(0, shown - offset)) });
    offset += it.text.length + 1;
  }
  return out;
}

export const joinItems = (items) => items.map((i) => i.text).join("\n");

export const TypedText = ({ text }) => (
  <>{text.slice(0, -1)}<span key={text.length} className="type-char">{text.slice(-1)}</span></>
);

export function useTypedLines(lines, interim) {
  const items = [...lines.map((l) => ({ key: `f${l.id}`, id: l.id, t: l.t, text: l.text, final: true })), ...(interim ? [{ key: "i", text: interim, final: false }] : [])];
  const full = joinItems(items);
  const shown = useTypewriter(full);
  return { typed: typedItems(items, shown), typing: shown < full.length, shown };
}

